import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useWebRTC } from '../context/WebRTCContext';
import { useFeature } from '../hooks/useFeatures';
import { SPECIAL_KEY_MAP, MODIFIER_KEYS, SpecialKeyBadge } from '../utils/keystroke-formatting';
import './SubjectStatusStrip.css';

/**
 * SubjectStatusStrip - Persistent awareness bar showing subject's live state
 *
 * Always visible below TopNav. Subscribes to ephemeral Sacred Timeline events
 * to show: current app, keystroke ticker, clipboard preview, active enforcements.
 */

const MAX_KEYSTROKE_CHARS = 120;

// Map narrative tags (from layered-sampler L2 taxonomy) to color buckets.
const INTEL_TAG_CATEGORY = {
    nsfw: 'critical',     dating: 'critical',
    gaming: 'warm',       media: 'warm',       video: 'warm',  music: 'warm',
    social: 'accent',     chat: 'accent',
    work: 'positive',     coding: 'positive',  writing: 'positive', reading: 'positive', creative: 'positive',
    browsing: 'neutral',  shopping: 'neutral', idle: 'neutral'
};
const intelTagCategory = (t) => INTEL_TAG_CATEGORY[t] || 'neutral';

// Plain-language connection quality — a non-technical operator reads "Strong"
// faster than "42ms". The exact latency stays in the tooltip.
const QUALITY_WORD = { good: 'Strong', fair: 'Weak', poor: 'Poor' };
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/**
 * Inline SVG icons for app types — consistent weight, inherits currentColor
 */
const AppIcon = ({ name }) => {
    const lower = (name || '').toLowerCase();
    // Browser
    if (lower.includes('chrome') || lower.includes('firefox') || lower.includes('edge') || lower.includes('brave'))
        return <svg className="strip-svg-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="8" cy="8" r="6.5"/><path d="M1.5 8h13M8 1.5c-2.5 2.5-2.5 10 0 13M8 1.5c2.5 2.5 2.5 10 0 13"/></svg>;
    // Chat apps
    if (lower.includes('discord') || lower.includes('slack') || lower.includes('teams'))
        return <svg className="strip-svg-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2.5 3.5h11v7.5c0 .5-.5 1-1 1h-3l-2.5 2v-2h-3.5c-.5 0-1-.5-1-1v-7.5z"/></svg>;
    // Code editors
    if (lower.includes('code') || lower.includes('vim') || lower.includes('idea') || lower.includes('studio'))
        return <svg className="strip-svg-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="12" height="12" rx="1.5"/><path d="M2 5h12"/><path d="M5 8l2 2-2 2M8.5 12h3"/></svg>;
    // File explorer
    if (lower.includes('explorer') || lower.includes('files'))
        return <svg className="strip-svg-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 4.5V12c0 .8.7 1.5 1.5 1.5h9c.8 0 1.5-.7 1.5-1.5V6c0-.8-.7-1.5-1.5-1.5H8L6.5 3H3.5C2.7 3 2 3.7 2 4.5z"/></svg>;
    // Music
    if (lower.includes('spotify') || lower.includes('music'))
        return <svg className="strip-svg-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10 3v7.5"/><circle cx="7.5" cy="11" r="2"/><path d="M14 2v7.5"/><circle cx="11.5" cy="10" r="2"/></svg>;
    // Games
    if (lower.includes('steam') || lower.includes('game'))
        return <svg className="strip-svg-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="1.5" y="4" width="13" height="8" rx="2"/><circle cx="5" cy="8" r="1.5"/><path d="M10 6.5v0M12 8v0M10 9.5v0"/></svg>;
    // Terminal
    if (lower.includes('terminal') || lower.includes('cmd') || lower.includes('powershell'))
        return <svg className="strip-svg-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2.5" width="12" height="11" rx="1.5"/><path d="M5 7l2.5 2L5 11M9 11h3"/></svg>;
    // Notepad / writing
    if (lower.includes('notepad') || lower.includes('word'))
        return <svg className="strip-svg-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="1.5" width="10" height="13" rx="1"/><path d="M6 5h4M6 8h4M6 11h2"/></svg>;
    // Image editors
    if (lower.includes('photoshop') || lower.includes('paint') || lower.includes('gimp'))
        return <svg className="strip-svg-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="8" cy="8" r="6"/><circle cx="8" cy="5" r="1.5"/><circle cx="5.5" cy="9.5" r="1.5"/><circle cx="10.5" cy="9.5" r="1.5"/></svg>;
    // Email
    if (lower.includes('mail') || lower.includes('outlook'))
        return <svg className="strip-svg-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="1.5" y="3.5" width="13" height="9" rx="1.5"/><path d="M1.5 3.5L8 9l6.5-5.5"/></svg>;
    // Default: monitor
    return <svg className="strip-svg-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="12" height="9" rx="1.5"/><path d="M5.5 14h5M8 11v3"/></svg>;
};

const SubjectStatusStrip = ({ children }) => {
    const { isConnected, connectedPeers, sendFeatureMessage, connectionStats, connectionState, lastDataReceived, reconnectAttempt } = useWebRTC();
    // useFeature polls until sacred-timeline registers (cold-load safe).
    const sacredFeature = useFeature('sacred-timeline');
    const hasDevice = connectedPeers && Object.keys(connectedPeers).length > 0;

    // Live state
    const [activityState, setActivityState] = useState('offline'); // active, typing, idle, locked, offline
    const [currentApp, setCurrentApp] = useState(null); // { name, title }
    const [keystrokeTokens, setKeystrokeTokens] = useState([]); // Array of { type, value, key? }
    const [clipboardText, setClipboardText] = useState('');
    const [enforcements, setEnforcements] = useState([]); // [{ type, label }]
    const [defianceLevel, setDefianceLevel] = useState(0); // 0-100

    const [keystrokeFlash, setKeystrokeFlash] = useState(false);
    const keystrokeFlashTimerRef = useRef(null);

    // Live AI screen classification (layered-sampler L2 → Haiku)
    const [intel, setIntel] = useState(null); // { activity, apps, tags, timestamp }
    const [intelClock, setIntelClock] = useState(() => Date.now());

    // Stale data detection
    const [isStale, setIsStale] = useState(false);
    useEffect(() => {
        if (!lastDataReceived) return;
        const interval = setInterval(() => {
            setIsStale(Date.now() - lastDataReceived > 30000);
        }, 5000);
        return () => clearInterval(interval);
    }, [lastDataReceived]);

    // Subscribe to Sacred Timeline events for live updates
    useEffect(() => {
        if (!isConnected || !sacredFeature) {
            setActivityState(isConnected && !sacredFeature ? (hasDevice ? 'active' : 'offline') : 'offline');
            return undefined;
        }

        setActivityState(hasDevice ? 'active' : 'offline');

        const handler = (eventData) => {
            const event = eventData.payload || eventData;
            if (!event || !event.type) return;

            const type = event.type;
            const data = { ...event, ...(event.data || {}) };

            // App focus changes
            if (type === 'activity.app_focused' || type === 'activity.app_launched') {
                setCurrentApp({
                    name: data.processName || data.appName || 'Unknown',
                    title: data.windowTitle || data.title || '',
                });
                setActivityState('active');
            }

            // Keystroke words
            if (type === 'activity.keystroke_word' || type === 'input.keystroke_session' || type === 'input.keystroke_batch') {
                const text = data.text || data.typedText || data.word || '';
                const keystrokes = data.keystrokes || [];
                if (text || keystrokes.length > 0) {
                    setKeystrokeTokens(prev => {
                        const newTokens = [];

                        // Process raw keystrokes array if available
                        if (keystrokes.length > 0) {
                            for (const ks of keystrokes) {
                                const key = ks.key || ks;
                                if (key === 'Backspace') {
                                    // Mark last non-deleted char as deleted
                                    for (let j = prev.length + newTokens.length - 1; j >= 0; j--) {
                                        const arr = j < prev.length ? prev : newTokens;
                                        const idx = j < prev.length ? j : j - prev.length;
                                        if (arr[idx].type === 'char') {
                                            arr[idx] = { ...arr[idx], type: 'deleted' };
                                            break;
                                        }
                                    }
                                } else if (MODIFIER_KEYS.has(key)) {
                                    // Skip modifiers
                                } else if (key === 'Enter' || key === '\n') {
                                    newTokens.push({ type: 'special', value: '↵', key: 'Enter' });
                                } else if (SPECIAL_KEY_MAP[key]) {
                                    newTokens.push({ type: 'special', value: SPECIAL_KEY_MAP[key], key });
                                } else if (key === ' ' || key === 'Space') {
                                    newTokens.push({ type: 'char', value: ' ' });
                                } else if (key.length === 1) {
                                    newTokens.push({ type: 'char', value: key });
                                }
                            }
                        } else {
                            // Fallback: process plain text, detect bracketed keys
                            const specialPattern = /(\[(?:Enter|Backspace|Delete|Tab|Escape|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Home|End|PageUp|PageDown|Space|F[1-9]|F1[0-2])\])/gi;
                            const parts = text.split(specialPattern);
                            for (const part of parts) {
                                if (!part) continue;
                                const bracketMatch = part.match(/^\[(.+)\]$/i);
                                if (bracketMatch) {
                                    const keyName = bracketMatch[1];
                                    const normalizedKey = Object.keys(SPECIAL_KEY_MAP).find(
                                        k => k.toLowerCase() === keyName.toLowerCase()
                                    );
                                    if (normalizedKey === 'Backspace') {
                                        for (let j = prev.length + newTokens.length - 1; j >= 0; j--) {
                                            const arr = j < prev.length ? prev : newTokens;
                                            const idx = j < prev.length ? j : j - prev.length;
                                            if (arr[idx].type === 'char') {
                                                arr[idx] = { ...arr[idx], type: 'deleted' };
                                                break;
                                            }
                                        }
                                    } else if (normalizedKey && !MODIFIER_KEYS.has(normalizedKey)) {
                                        newTokens.push({ type: 'special', value: SPECIAL_KEY_MAP[normalizedKey], key: normalizedKey });
                                    }
                                } else {
                                    for (const ch of part) {
                                        newTokens.push({ type: 'char', value: ch });
                                    }
                                }
                            }
                        }

                        const combined = [...prev, ...newTokens];
                        return combined.length > MAX_KEYSTROKE_CHARS
                            ? combined.slice(-MAX_KEYSTROKE_CHARS)
                            : combined;
                    });
                    setActivityState('typing');
                    setKeystrokeFlash(true);
                    clearTimeout(keystrokeFlashTimerRef.current);
                    keystrokeFlashTimerRef.current = setTimeout(() => setKeystrokeFlash(false), 300);
                }
            }

            // Clipboard changes
            if (type === 'activity.clipboard_copied' || type === 'clipboard.text_copied') {
                const text = data.text || data.content || '';
                if (text) {
                    setClipboardText(text.slice(0, 100));
                }
            }

            // Idle state
            if (type === 'activity.idle_started' || type === 'activity.idle_start') {
                setActivityState('idle');
            }
            if (type === 'activity.idle_ended' || type === 'activity.idle_end') {
                setActivityState('active');
            }

            // Lockout / enforcement changes
            if (type === 'punishment.lockout_started') {
                setEnforcements(prev => [...prev.filter(e => e.type !== 'lockout'), { type: 'lockout', label: 'Lockout' }]);
            }
            if (type === 'punishment.lockout_ended') {
                setEnforcements(prev => prev.filter(e => e.type !== 'lockout'));
            }
            if (type === 'punishment.input_sabotaged') {
                setEnforcements(prev => [...prev.filter(e => e.type !== 'sabotage'), { type: 'sabotage', label: 'Sabotage' }]);
            }
            if (type === 'punishment.write_lines_assigned') {
                setEnforcements(prev => [...prev.filter(e => e.type !== 'lines'), { type: 'lines', label: 'Write Lines' }]);
            }
            if (type === 'punishment.write_lines_completed') {
                setEnforcements(prev => prev.filter(e => e.type !== 'lines'));
            }
            if (type === 'interrogation.session_started') {
                setEnforcements(prev => [...prev.filter(e => e.type !== 'interrogation'), { type: 'interrogation', label: 'Interrogation' }]);
            }
            if (type === 'interrogation.session_ended') {
                setEnforcements(prev => prev.filter(e => e.type !== 'interrogation'));
            }

            // Defiance level from chat AI
            if (type === 'chat.message_sent' && data.defiance_level !== undefined) {
                setDefianceLevel(Math.min(100, Math.max(0, data.defiance_level * 10)));
            }

            // AI screen classification (Haiku L2)
            if (type === 'narrative.classification') {
                setIntel({
                    activity: String(data.activity || '').trim(),
                    apps: Array.isArray(data.apps) ? data.apps : [],
                    tags: Array.isArray(data.tags) ? data.tags.map(t => String(t).toLowerCase()) : [],
                    timestamp: event.timestamp || Date.now()
                });
            }
        };

        const unsubLive = sacredFeature.on('sacred_event', handler);

        return () => {
            unsubLive();
            clearTimeout(keystrokeFlashTimerRef.current);
        };
    }, [sacredFeature, isConnected, hasDevice]);

    // Typing state auto-decays to active after 3s of no keystrokes
    useEffect(() => {
        if (activityState !== 'typing') return;
        const timeout = setTimeout(() => setActivityState('active'), 3000);
        return () => clearTimeout(timeout);
    }, [activityState, keystrokeTokens]);

    // Drive intel freshness label (Xs · stale · paused)
    useEffect(() => {
        const id = setInterval(() => setIntelClock(Date.now()), 5000);
        return () => clearInterval(id);
    }, []);

    // Derive intel display values once per render
    const intelTags = intel?.tags || [];
    const intelIsNsfw = intelTags.includes('nsfw');
    const intelAgeMs = intel ? intelClock - intel.timestamp : null;
    const intelIsStale = intelAgeMs !== null && intelAgeMs > 90000;
    const intelIsPaused = activityState === 'idle';
    let intelFreshness;
    if (intelIsStale) intelFreshness = 'stale';
    else if (intel && intelAgeMs < 5000) intelFreshness = 'now';
    else if (intel) intelFreshness = `${Math.round(intelAgeMs / 1000)}s`;
    else intelFreshness = null;

    // Tooltip content — full narrative on hover, fallback to status text
    let intelTooltip;
    if (intel?.activity) intelTooltip = intel.activity;
    else if (intelIsPaused) intelTooltip = 'AI screen classification paused — subject is idle';
    else intelTooltip = 'Awaiting first AI screen classification (runs every 30s)';

    // Map defiance level to border color
    const defianceBorderColor = defianceLevel < 30
        ? 'var(--status-success)'
        : defianceLevel < 70
            ? 'var(--status-warning)'
            : 'var(--status-error)';

    if (!hasDevice) {
        return null;
    }

    return (
        <div
            className={`subject-strip${isStale ? ' strip-stale' : ''}`}
            style={{ '--defiance-color': defianceBorderColor }}
        >
            {/* Activity State */}
            <div className="strip-state" data-tooltip="Subject's current activity state">
                <span className={`strip-state-dot state-${activityState}`} />
                <span className="strip-state-label">{cap(activityState)}</span>
            </div>

            {/* Connection Quality */}
            {connectionStats && connectionStats.quality !== 'unknown' && (
                <div
                    className={`strip-connection-quality quality-${connectionStats.quality}`}
                    data-tooltip={connectionStats.quality === 'disconnected'
                        ? 'Connection lost — attempting to reconnect'
                        : `Connection: ${QUALITY_WORD[connectionStats.quality] || cap(connectionStats.quality)} (${connectionStats.latency}ms)`}
                >
                    <span className="strip-quality-dot" />
                    {connectionStats.quality === 'disconnected' ? (
                        <span className="strip-quality-label">
                            Reconnecting{reconnectAttempt > 0 ? ` (${reconnectAttempt}/10)` : ''}
                        </span>
                    ) : (
                        <span className="strip-quality-label">{QUALITY_WORD[connectionStats.quality] || cap(connectionStats.quality)}</span>
                    )}
                </div>
            )}

            {/* Stale Data Warning */}
            {isStale && lastDataReceived && (
                <div className="strip-stale-indicator" data-tooltip="No data received recently">
                    Last update: {Math.round((Date.now() - lastDataReceived) / 1000)}s ago
                </div>
            )}

            {/* Current App — promoted as primary info */}
            {currentApp && (
                <div className="strip-app" data-tooltip="Currently focused application">
                    <AppIcon name={currentApp.name} />
                    <span className="strip-app-name">{currentApp.name}</span>
                    {currentApp.title && (
                        <span className="strip-app-title">— {currentApp.title}</span>
                    )}
                </div>
            )}

            {/* AI INTEL — compact pill; hover for full Haiku narrative */}
            <div
                className={`strip-intel${intelIsNsfw ? ' intel-nsfw' : ''}${intelIsStale ? ' intel-stale' : ''}${intelIsPaused ? ' intel-paused' : ''}${!intel ? ' intel-empty' : ''}`}
                data-tooltip={intelTooltip}
                data-tooltip-pos="bottom"
            >
                <span className="strip-intel-eyebrow">
                    <span className="strip-intel-tick" />
                    <span className="strip-intel-label">AI</span>
                </span>
                {intelIsNsfw && <span className="strip-intel-alert">EXPOSURE</span>}
                {intelTags.length > 0 ? (
                    <span className="strip-intel-tags">
                        {intelTags.slice(0, 3).map(t => (
                            <span key={`itag-${t}`} className={`strip-intel-tag intel-tag-${intelTagCategory(t)}`}>
                                {t}
                            </span>
                        ))}
                    </span>
                ) : (
                    <span className="strip-intel-status">
                        {intelIsPaused ? 'paused' : 'awaiting'}
                    </span>
                )}
                {intelFreshness && (
                    <span className="strip-intel-freshness">{intelFreshness}</span>
                )}
            </div>

            {/* Keystroke Ticker — hidden when empty */}
            {keystrokeTokens.length > 0 && (
                <div className="strip-keystrokes" data-tooltip="Live keystrokes captured from the desktop">
                    <svg className="strip-svg-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="1.5" y="4" width="13" height="9" rx="1.5"/>
                        <path d="M4 7.5h1M7 7.5h2M11 7.5h1M5 10h6"/>
                    </svg>
                    <span className={`strip-keystrokes-text ${keystrokeFlash ? 'has-new' : ''}`}>
                        {keystrokeTokens.map((token, i) => {
                            if (token.type === 'special') {
                                return <SpecialKeyBadge key={i} keyName={token.key} symbol={token.value} className="strip-key-badge" />;
                            }
                            if (token.type === 'deleted') {
                                return <span key={i} className="strip-char-deleted">{token.value}</span>;
                            }
                            return <span key={i}>{token.value}</span>;
                        })}
                    </span>
                </div>
            )}

            {/* Clipboard Preview — hidden when empty */}
            {clipboardText && (
                <div className="strip-clipboard" data-tooltip="Last copied clipboard content">
                    <svg className="strip-svg-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="4" y="1.5" width="8" height="3" rx="0.5"/>
                        <path d="M11.5 3H12.5C13.3 3 14 3.7 14 4.5V13c0 .8-.7 1.5-1.5 1.5h-9C2.7 14.5 2 13.8 2 13V4.5C2 3.7 2.7 3 3.5 3H4.5"/>
                    </svg>
                    <span className="strip-clipboard-text">{clipboardText}</span>
                </div>
            )}

            {/* Active Enforcements */}
            {enforcements.length > 0 && (
                <div className="strip-enforcements" data-tooltip="Active punishments and enforcement actions">
                    {enforcements.map(e => (
                        <span key={e.type} className={`strip-enforcement-pill enforce-${e.type}`} data-tooltip={`Active: ${e.label}`}>
                            {e.label}
                        </span>
                    ))}
                </div>
            )}

            {/* AI Controls (embedded from AIControlBar) */}
            {children && (
                <div className="strip-ai-controls">
                    {children}
                </div>
            )}

        </div>
    );
};

export default SubjectStatusStrip;
